import type { NavGroup, Section } from "./facility.types";

// Facility nav: the pool and the line secured on it. Anything read against a
// signed term (covenants, cash, drawn balance, order of application) is read on
// the contract, in the Deal cockpit, because an investor holds every facility
// under it. SECTION_REDIRECTS maps old bookmarks.
//
// Ledgers is admin-only: where the line's movements, the notes' amortisations
// and the contributed cash are keyed. The Deal's Obligation reads them, writes
// none. It is not in NON_ADMIN_SECTIONS, so a grant reaches it neither by nav
// nor by URL.
//
// FACILITY_SECTIONS is every section a URL resolves to. Titles, URL resolution
// and restrictions read it.
const FACILITY_SECTIONS: NavGroup["items"] = [
  { id: "overview", label: "Overview", ready: true },
  { id: "collateral", label: "Collateral", ready: true },
  { id: "obligation", label: "Ledgers", ready: true },
];

export const FACILITY_NAV: NavGroup = {
  label: "Facility",
  items: FACILITY_SECTIONS,
};

// What a deal_view / deals_view grant opens. Both name a deal and reach every
// facility under it (`canViewDeal(perms, facility.deal_id)` in FacilityPage).
//
// An allowlist, so a section added to the nav has to state here whether a
// counterparty reaches it; the default is that it does not.
//
// The list is the surface, not the enforcement: FacilityPage blocks the same
// ids by URL through `restrictedSections`, and every endpoint carries its own guard.
const NON_ADMIN_SECTIONS = new Set<string>(["overview", "collateral"]);

export const NON_ADMIN_FACILITY_NAV: NavGroup = {
  label: "Facility",
  items: FACILITY_NAV.items.filter((i) => NON_ADMIN_SECTIONS.has(i.id)),
};

// Sections hidden from a guest under an anonymous grant: those whose payload
// has no anonymous form (the server answers 403). None today; the set keeps
// the rule in place.
const ANON_SECTIONS = new Set<string>([]);

export const ANON_FACILITY_NAV: NavGroup = {
  label: "Facility",
  items: NON_ADMIN_FACILITY_NAV.items.filter((i) => !ANON_SECTIONS.has(i.id)),
};

/** Sections a non-admin never reaches, by crafted URL either — the mirror of
 *  the two navs above, so one decision is not spelled out twice by hand. */
export function restrictedSections(anonymous: boolean): Set<string> {
  const allowed = anonymous ? ANON_FACILITY_NAV.items : NON_ADMIN_FACILITY_NAV.items;
  const ids = new Set(allowed.map((i) => i.id));
  return new Set(FACILITY_SECTIONS.map((i) => i.id).filter((id) => !ids.has(id)));
}

// A section's page title is its nav label, so sidebar and header cannot
// disagree. Partial: `Section` also lists ids that no longer render.
export const SECTION_TITLES: Partial<Record<Section, string>> = Object.fromEntries(
  FACILITY_SECTIONS.map((i) => [i.id, i.label]),
);

// Legacy ?section= ids, so old bookmarks and external links still resolve.
//  · Overview: the landing, the KPIs and the FX/hedge reading
//  · Collateral: the BB cascade, concentration, pledged-pool and descriptive
//    pool views
//
// Contract-level sections (covenants, waterfall, cash) and incidents land on
// Overview, not the Deal: this map is a pure function of the id and does not
// know the facility's contract. Overview links up to it.
//
// Config, Data Room and Upload Files only left the nav: Config has its own
// route (FacilityPage forwards an admin's bookmark there); Data Room and Upload
// Files are book-scoped, reachable from the book.
export const SECTION_REDIRECTS: Record<string, Section> = {
  key_indicators: "overview",
  fx_risk: "overview",
  borrowing_base: "collateral",
  concentration: "collateral",
  characteristics: "collateral",
  portfolio_concentration: "collateral",
  facility_delinquency: "collateral",
  covenants: "overview",
  cash: "overview",
  reconciliation: "overview",
  bank_statement: "overview",
  // Former id of the obligation section.
  facility: "obligation",
  waterfall: "overview",
  incidents: "overview",
  config: "overview",
  dataroom: "overview",
  data_room: "overview",
  upload: "overview",
  // Former portfolio and analytics ids land on Collateral; deep longitudinal
  // analytics stay on the originator book.
  portfolio: "collateral",
  traction: "collateral",
  delinquency: "collateral",
  collection: "collateral",
  curves: "collateral",
  cohort: "collateral",
  yields: "collateral",
  cashflow: "collateral",
  performance: "collateral",
};

// Whether a facility is read against note terms (balance/rate/close date) or
// line terms (size/advance rate/first draw).
// Must be an allowlist of securitization types, never `!== "WAREHOUSE"`: the
// backend column is a free-form string (models/facility.py FACILITY_TYPES is
// not enforced), so values like "REVOLVING_WAREHOUSE" arrive and must not be
// read as securitizations. Anything unrecognised is treated as WAREHOUSE.
const SECURITIZATION_TYPES = new Set([
  "TERM_SECURITIZATION",
  "REVOLVING_SECURITIZATION",
  "SECURITIZATION",
]);

export function isSecuritization(facilityType: string | null | undefined): boolean {
  return SECURITIZATION_TYPES.has((facilityType ?? "").toUpperCase());
}

const CANONICAL_SECTIONS = new Set<string>(FACILITY_SECTIONS.map((i) => i.id));

/**
 * Resolve any ?section= value to a canonical facility section.
 * Canonical ids pass through; legacy ids redirect via SECTION_REDIRECTS;
 * unknown/empty falls back to the overview landing.
 */
export function resolveSection(raw: string | null | undefined): Section {
  if (raw && CANONICAL_SECTIONS.has(raw)) return raw as Section;
  if (raw && raw in SECTION_REDIRECTS) return SECTION_REDIRECTS[raw];
  return "overview";
}

export const PAR_COLORS: Record<string, string> = {
  current: "#059669",
  par_1_30: "#D97706",
  par_31_60: "#EA580C",
  par_61_90: "#DC2626",
  par_90_plus: "#7F1D1D",
};

export const PAR_LABELS: Record<string, string> = {
  current: "Current",
  par_1_30: "PAR 1-30",
  par_31_60: "PAR 31-60",
  par_61_90: "PAR 61-90",
  par_90_plus: "PAR 90+",
};

export const DPD_COLORS: Record<string, string> = {
  CURRENT: "#059669",
  "1-30": "#D97706",
  "31-60": "#EA580C",
  "61-90": "#DC2626",
  "91-120": "#B91C1C",
  "121-180": "#991B1B",
  "181-360": "#7F1D1D",
  "360+": "#450A0A",
};

export const COHORT_BUCKET_COLORS: Record<string, string> = {
  CURRENT: "#059669",
  "1-30": "#D97706",
  "31-60": "#EA580C",
  "61-90": "#DC2626",
  "90+": "#7F1D1D",
  REPAID: "#6B7280",
  // A live loan whose tape reported no DPD: its own bucket, never folded into
  // CURRENT (nobody said it is up to date) nor into 90+ (the SQL ladder's ELSE).
  UNREPORTED: "#9CA3AF",
};

export const COHORT_BUCKETS = ["CURRENT", "1-30", "31-60", "61-90", "90+", "UNREPORTED", "REPAID"] as const;

const DPD_ORDER = ["CURRENT", "1-30", "31-60", "61-90", "91-120", "121-180", "181-360", "360+"];
const TENOR_ORDER = ["≤6m", "7-12m", "13-18m", "19-24m", "25-30m", "31-36m", "37-42m", "43-48m", "49-60m", "61-72m", "72m+"];
const AMOUNT_BUCKET_ORDER = ["0-3M", "3-5M", "5-7M", "7-10M", "10M+"];

export function sortDPD<T extends { bucket: string }>(buckets: T[]): T[] {
  return [...buckets].sort((a, b) => DPD_ORDER.indexOf(a.bucket) - DPD_ORDER.indexOf(b.bucket));
}

export function tenorOrder(bucket: string): number {
  return TENOR_ORDER.indexOf(bucket);
}

export function bucketOrder(bucket: string): number {
  return AMOUNT_BUCKET_ORDER.indexOf(bucket);
}

// `wac` is already an annual decimal (the backend annualizes interest_rate at
// ingest): pass it through, never × 12.
export function getWacAnnual(ki: { wac?: number | null } | null | undefined): number | null {
  return ki?.wac ?? null;
}
